import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Tag } from "@ts-digital/vrc";
import { FormattedMessage, useIntl } from "react-intl";
import { dictionary } from "../../constants/dictionary";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { renderIcon } from "../../libs/deadlinesUtils";
import { Input } from "antd";
import moment from "moment";
import { editDocumentFilename } from "../../store/actions/documents/renameDocuments";
import ErrorNotification from "./errorNotification";
import { FEATURE_TOGGLE_DOCUMENTS_FILENAME_SECTION } from "../../config";

import "./style.css";

/**
 * In origine erano tag con background verdi che, risultati illegibili, sono diventati
 * delle etichette semplici di puro testo
 */
const Tags = () => {
  const info = useSelector(state => state.detailsDocument.info);
  const renameDocument = useSelector(state => state.detailsDocument.renameDocument);
  const dispatch = useDispatch();
  const [newFilename, setNewFilename] = useState("");
  const [copySuccess, setCopySuccess] = useState("");
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [originalNameStyles, setOriginalNameStyles] = useState({});
  const renamedFileName = info.details.renamedFileName;
  const isErrorRename = renameDocument.status.error === true ? true : false;

  const fileName = info.details.fileName;
  const defaultFileName = fileName.substring(0,
    fileName.indexOf(fileName.split(".").pop()) - 1);
  const extension = fileName.split(".").pop();

  const inputPlaceholder = useIntl().formatMessage({id: "documents.detail.inputPlaceholder"});

  useEffect(() => {
    setOriginalNameStyles({
      display: renamedFileName !== null ? "block" : "none"
    });
  }, [renamedFileName]);

  useEffect(() => {
    if (isErrorRename) {
      setShowErrorModal(true);
    }
  }, [renameDocument]);

  let arrayTagsDefault = [];
  let arrayTagPersonalized = [];

  const copyToClipboard = value => {
    const textField = document.createElement("textarea");
    textField.innerText = value;
    document.body.appendChild(textField);
    textField.select();
    document.execCommand("copy");
    textField.remove();
    setCopySuccess("Copiato");
  };

  const findTag = tag => {
    return info.details.standardTags.find(el => {
      return el.key === tag;
    });
  };

  const dataFormatter = (unixData, excludeTime) => {
    let value = null;
    if (unixData) {
      const data = new Date(Number(unixData));
      if (data) {
        if (excludeTime) {
          value = "il " + moment(data).format("DD MMM YYYY");
        } else {
          value = "il " + moment(data).format("DD MMM YYYY [ore] HH:mm");
        }
      }
    }
    return value;
  };
  // TIPI DI DATI CHE OTTENIAMO DAI METADATI :
  // 1) private Long receiptDate; // data ora ricezione SDI

  // 2) private Long sdiDeliveryDate; // data ora di invio a SDI

  // 3) private Long sdiReceiptDate; // data ora di ricezione da parte di SDI, letta dalle notifiche

  // 4) private Long recipientDeliveryDate; // data ora di consegna al destinatario da parte  di SDI

  // 5) private Long recipientAvailableDate; // data di messa a disposizione sul cassetto fiscale in caso di mancata consegna

  // La 1 la otteniamo per le ricevute, la 2 c'è sempre, la 3 non serve, la 4 e la 5 è per le emesse. Nel caso di receiptDate per le emesse, non considerarlo!

  //   stati ritornati attualmente dal CORE:
  // public static boolean isValidStatus(String status) {
  //    return List.of("INVIATO", "A_DISPOSIZIONE", "SCARTATO", "NON_RECAPITATO", "IN_GESTIONE", "AUTO_INVIATO")
  //          .contains(status);
  // }   per la PA invece
  //  ACCETTATO RIFIUTATO NESSUNA_RISPOSTA
  const getStatus = (
    iconColor, iconTitle, description, text
  ) => {
    return {
      color: iconColor,
      name: iconTitle,
      details: description,
      additionalText: text
    };
  };

  const renderStatus = () => {
    let status = null;
    if (!info.details.einvoice) {
      return getStatus(
        "", "", "", ""
      );
    }

    const emessa = info.details.categoryId === "fatture.emesse.b2b";
    const ricevuta = info.details.categoryId === "fatture.ricevute.b2b";

    const yellow = "rgb(253, 185, 39)";
    const yellowIconName = "arrow-circle-right";
    const green = "rgb(9, 130, 42)";
    const greenIconName = "check-circle";
    const red = "rgb(216, 40, 41)";
    const redIconName = "times-circle";

    if (info.details.standardTags) {
      if (emessa) {
        const recipientDeliveryDate = findTag("recipient.deliveryDate");
        const statusName = findTag("status.name");
        const statusDate = findTag("status.timestamp");

        if (recipientDeliveryDate) {
          if (
            (statusName && statusName.value === "RIFIUTATO") ||
            statusName.value === "ACCETTATO" ||
            statusName.value === "NESSUNA_RISPOSTA"
          ) {
            if (statusName.value === "RIFIUTATO") {
              status = getStatus(
                red,
                redIconName,
                "documents.detail.paRejected",
                dataFormatter(statusDate ? statusDate.value : null)
              );
            } else if (statusName.value === "ACCETTATO") {
              status = getStatus(
                green,
                greenIconName,
                "documents.detail.paAccepted",
                dataFormatter(statusDate ? statusDate.value : null)
              );
            } else if (statusName.value === "NESSUNA_RISPOSTA") {
              status = getStatus(
                yellow,
                yellowIconName,
                "documents.detail.paEffectiveDate",
                dataFormatter(statusDate ? statusDate.value : null)
              );
            }
          }
          if (status == null) {
            status = getStatus(
              green,
              greenIconName,
              "documents.detail.recipientDeliveryDate",
              dataFormatter(recipientDeliveryDate.value)
            );
          }
        } else {
          const recipientAvailableDate = findTag("recipient.availableDate");
          if (recipientAvailableDate) {
            status = getStatus(
              green,
              greenIconName,
              "documents.detail.recipientAvailableDate",
              dataFormatter(recipientAvailableDate.value, true)
            );
          }
        }
      } else if (ricevuta) {
        const receiptDate = findTag("receiptDate");
        if (receiptDate) {
          status = getStatus(
            green,
            greenIconName,
            "documents.detail.receiptDate",
            dataFormatter(receiptDate.value)
          );
        }
      }

      // Se non ho ancora trovato nulla, controllo almeno la data di Consegna allo sdi (questa dovrebbe esserci sempre)

      if (status == null) {
        const sdiDeliveryDate = findTag("sdi.deliveryDate");
        if (sdiDeliveryDate) {
          status = getStatus(
            yellow,
            yellowIconName,
            "documents.detail.sdiDeliveryDate",
            dataFormatter(sdiDeliveryDate.value)
          );
        } else status = getStatus(
          "", "", "", ""
        );
      }
    } else status = getStatus(
      "", "", "", ""
    );

    return status;
  };

  if (info.status.started) {
    return <FontAwesomeIcon icon={["far", "circle-notch"]} spin />;
  } 
  if (info.status.ended && info.details) {
    if (info.details.standardTags) {
      /** Per Lynfa arriva un tag chiamato 'documento.tipo', se presente, il valore deve sostituire la category  */
      let lynfaDocType = info.details.standardTags.find(el => {
        return el.key === "documento.tipo" || el.key === "documento_tipo";
      });
      lynfaDocType = lynfaDocType ? lynfaDocType.value : null;
      let standardTagsFiltered = info.details.standardTags;
      if (lynfaDocType) {
        // rimuoviamo il tag di troppo
        standardTagsFiltered = info.details.standardTags.filter(el => {
          return el.key !== "documento.tipo" && el.key !== "documento_tipo";
        });
        // sostituisco al value della category(tipo documento) il valore da Lynfa
        let realDocumentType = info.details.standardTags.find(el => {
          return el.key === "category";
        });
        realDocumentType["value"] = lynfaDocType;
      }
      let causaliList = "";
      standardTagsFiltered.forEach(function(key, index) {
        let keyTags = standardTagsFiltered[index].key
          ? standardTagsFiltered[index].key
          : null;
        let tagsValue = standardTagsFiltered[index].value;
        if (
          info.details.einvoice &&
            keyTags !== null &&
            keyTags.startsWith("causali")
        ) {
          causaliList = causaliList + tagsValue + ", ";
        }

        if (
          (info.details.einvoice &&
              keyTags !== null &&
              keyTags !== "macrocategory" &&
              keyTags !== "microcategory") ||
            keyTags === "PeriodoImposta" ||
            keyTags === "periodo" ||
            keyTags === "importoTotaleDocumento"
        )
          return;
        keyTags =
            keyTags && dictionary[keyTags.toLowerCase()]
              ? dictionary[keyTags.toLowerCase()]
              : keyTags;
        
        // keyTags = keyTags ? keyTags.replace(/([A-Z])/g, " $1").trim() : "";

        keyTags = keyTags ? <FormattedMessage defaultMessage={standardTagsFiltered[index].key} id={`documents.dictionary.${standardTagsFiltered[index].key?.toLowerCase()}`} /> : "";

        tagsValue =
            tagsValue && dictionary[tagsValue.toLowerCase()]
              ? dictionary[tagsValue.toLowerCase()]
              : tagsValue;

        if (
          standardTagsFiltered[index].key === "ProtocolloEntratelFisconline"
        ) {
          tagsValue = (
            <div style={{ display: "flex", alignItems: "center" }}>
              {standardTagsFiltered[index].value}
              <div
                style={{
                  cursor: "pointer",
                  margin: "0 10px",
                  color: "#0090d1"
                }}
                onClick={() =>
                  copyToClipboard(standardTagsFiltered[index].value)
                }
              >
                <FontAwesomeIcon
                  icon={["far", "copy"]}
                  style={{
                    width: "22px",
                    height: "22px",
                    color: "#0090d1"
                  }}
                />
              </div>
              {copySuccess}
            </div>
          );
        }
        arrayTagsDefault.push(<div className="textualTag" key={"textTag_" + keyTags + index}>
          {keyTags}: <b style={{ marginLeft: "5px" }}> {tagsValue}</b>
        </div>);
      });

      if (causaliList !== "" && info.details.einvoice) {
        arrayTagsDefault.push(<div className="textualTag" key={"textTag_causale"}>
          {"Causale"}:{" "}
          <b style={{ marginLeft: "5px" }}>
            {causaliList.substring(0, causaliList.length - 2)}
          </b>
        </div>);
      }
    }
    if (info.details.customTags)
      info.details.customTags.forEach(function(item) {
        arrayTagPersonalized.push(<Tag key={"tag_" + item} color="RoyalFuchsia" type="standard">
          {item}
        </Tag>);
      });
  }

  const { color, name, details, additionalText } = renderStatus();

  const focusMethod = () => {
    const renameInput = document.getElementById("renameInput");
    renameInput.focus();
  };

  const onChangeFileName = e => {
    const insertedValue = e.target.value;
    setNewFilename(insertedValue + "." + extension);
    setShowErrorModal(insertedValue.length === 0 && true);
  };
  const onSubmitChangeFilename = (documentId,
    newFilename) => {
    dispatch(editDocumentFilename(documentId, newFilename));
  };
  const onCloseErrorModal = () => {
    setShowErrorModal(false);
  };
  const onSubmitRenameFile = () => {
    if (newFilename.length > 0) {
      onSubmitChangeFilename(documentId,
        newFilename);
    } else {
      setShowErrorModal(true);
    }
  };

  const documentId = info.details.documentId;
  const isLoading =
    renameDocument.status.started === true &&
    renameDocument.status.error === false
      ? true
      : false;

  return (
    <div>
      {info.details.einvoice &&
      color !== "" &&
      name !== "" &&
      details !== "" ? (
          <div className="borderSeparatorSection">
            <div className="detailSectionTitle">
              <FormattedMessage id="documents.detail.status" />
            </div>
            {renderIcon(
              color, name, details, additionalText
            )}
          </div>
        ) : null}
      <div className="borderSeparatorSection">
        <div className="detailSectionTitle">
          <FormattedMessage id="documents.detail.infoSectionTitle" />
        </div>
        {arrayTagsDefault}
        <div key={info.details.uploader.fullName} className="detailCard">
          <FormattedMessage id="documents.detail.uploader" /> :{" "}
          <b>
            {info.details.uploader.fullName} (
            {info.details.uploader.companyName})
          </b>
        </div>
      </div>
      {FEATURE_TOGGLE_DOCUMENTS_FILENAME_SECTION === "true" && (
        <div className="filenameSection">
          <div className="detailSectionTitle">
            <FormattedMessage id="documents.detail.nameSectionTitle" />
          </div>
          <Input
            defaultValue={defaultFileName}
            placeholder={inputPlaceholder}
            id="renameInput"
            addonAfter={
              !isLoading ? (
                <FontAwesomeIcon
                  onClick={() => focusMethod()}
                  icon={["far", "edit"]}
                  style={{ color: "0090d1" }}
                />
              ) : null
            }
            onPressEnter={onSubmitRenameFile}
            onChange={e => onChangeFileName(e)}
            style={{
              borderLeftColor: isLoading && "white",
              borderRightColor: isLoading && "white",
              borderTopColor: isLoading && "white"
            }}
          />

          <p className="originalName" style={originalNameStyles}>
            <FormattedMessage id="documents.detail.originalName" /> :{" "}
            {renamedFileName}{" "}
          </p>

          <ErrorNotification
            duration={2147483647}
            title={<FormattedMessage id="documents.detail.errorTitle" />}
            message={
              isErrorRename ? (
                <FormattedMessage id="documents.detail.errorMessageInternalServerError" />
              ) : (
                <FormattedMessage id="documents.detail.errorMessageNoCharacter" />
              )
            }
            show={showErrorModal}
            type="error"
            hideToast={onCloseErrorModal}
          />
        </div>
      )}

      <div style={{ marginTop: "32px" }}>
        {arrayTagPersonalized.length > 0 ? (
          <span style={{ fontWeight: "normal", lineHeight: "32px" }}>
            <FormattedMessage id="documents.classificator.tags.custom" />:{" "}
          </span>
        ) : null}
        <br />
        {arrayTagPersonalized}
      </div>
    </div>
  );
};

export default Tags;
